// The Insights pages, ported from CGS2022. This one table drives the sidebar group
// (navConfig.js), the top-bar dropdown (header.vue), the routes (router/index.js) and the
// route guard. So a page's label, address and role gate are each stated once.
//
// ── Feature flag ────────────────────────────────────────────────────────────────────────
// Default OFF. On per build with VUE_APP_INSIGHTS=1, or per browser for testing without a
// rebuild with localStorage.setItem("insights.enabled", "1"). As with nav.sidebar.enabled,
// the localStorage value wins when it's set, so a tester can also force it off.
//
// ── Who sees what ───────────────────────────────────────────────────────────────────────
// These are the CGS2022 menu rules (Views/Shared/_NavPartial.cshtml). Like CGS, they only
// decide what the menu shows. The API scopes the data itself (by the azureId sent with every
// query and the GetInsightHeader branch list), so these gates are not a security boundary,
// and they weren't one in CGS either.
//
//   corp     Executive Dashboard, Warehouse Analysis, Loan Officer Analysis,
//            Scenario Builder Metrics, Support Metrics, Weekly Summary
//   revenue  Revenue Analysis - Active Locks: Margin Manager or Insights Branch Manager.
//            Corp alone does NOT open it in CGS, and that's kept here.
//   baldwin  Baldwin Analysis: Baldwin Insights Manager only (Corp alone does not open it).
//   (none)   everything else
//
// Support Metrics and Weekly Summary: CGS hides "Notifications Sent" and "Ticket Insights"
// from non-corp users by name. Neither name is in the menu any more (one is commented out and
// the other was renamed Support Metrics). findIndex then returns -1, and splice(-1, 1)
// removes the LAST item, twice. That takes off Weekly Summary and then Support Metrics.
// "Ticket Insights" was meant to be corp-only, so Support Metrics being corp-only is
// intended. Weekly Summary being corp-only is an accident, kept so that nobody gains or loses
// a page in the move. To open it to everyone, set WEEKLY_SUMMARY_GATE to null.

export const INSIGHTS_FLAG_KEY = 'insights.enabled'

export function isInsightsEnabled() {
  try {
    const override = window?.localStorage?.getItem(INSIGHTS_FLAG_KEY)
    if (override === '1') return true
    if (override === '0') return false
  } catch (e) {}
  return process.env.VUE_APP_INSIGHTS === '1'
}

export const WEEKLY_SUMMARY_GATE = 'corp'

export const INSIGHTS_BASE = '/analytics'

// section: the heading the page sits under in the menu.
// cgs: the CGS2022 InsightsController action (and Views/Insights/<cgs>.cshtml).
// title: the insightsTitle CGS passes the page, which a few page and filter rules key on.
export const INSIGHT_PAGES = [
  { slug: 'active-pipeline', label: 'Active Pipeline', title: 'Active Pipeline Analysis', section: 'Pipeline', gate: null, cgs: 'ActivePipelineAnalysis', icon: 'fa-light fa-chart-column' },
  { slug: 'applications', label: 'Applications Analysis', title: 'Applications Analysis', section: 'Pipeline', gate: null, cgs: 'ApplicationsAnalysis', icon: 'fa-light fa-file-signature' },
  { slug: 'locks', label: 'Locks Analysis', title: 'Locks Analysis', section: 'Pipeline', gate: null, cgs: 'LocksAnalysis', icon: 'fa-light fa-lock' },
  { slug: 'loan-risk', label: 'Loan Risk Analysis', title: 'Loan Risk Analysis', section: 'Pipeline', gate: null, cgs: 'LoanRiskAnalysis', icon: 'fa-light fa-triangle-exclamation' },

  { slug: 'origination', label: 'Origination', title: 'Origination', section: 'Production', gate: null, cgs: 'Origination', icon: 'fa-light fa-chart-line-up' },
  { slug: 'funding', label: 'Funding Analysis', title: 'Funding Analysis', section: 'Production', gate: null, cgs: 'FundingAnalysis', icon: 'fa-light fa-sack-dollar' },
  { slug: 'loan-officers', label: 'Loan Officer Analysis', title: 'Loan Officer Analysis', section: 'Production', gate: 'corp', cgs: 'LoanOfficerAnalysis', icon: 'fa-light fa-user-tie' },
  { slug: 'performance-scorecard', label: 'Performance Scorecard', title: 'Performance Scorecard', section: 'Production', gate: null, cgs: 'PerformanceScorecard', icon: 'fa-light fa-gauge-high' },
  { slug: 'weekly-summary', label: 'Weekly Summary', title: 'Weekly Summary', section: 'Production', gate: WEEKLY_SUMMARY_GATE, cgs: 'WeeklySummary', icon: 'fa-light fa-calendar-week' },

  { slug: 'revenue-active-locks', label: 'Revenue - Active Locks', title: 'Revenue Analysis - Active Locks', section: 'Revenue & capital', gate: 'revenue', cgs: 'RevenueAnalysisActiveLocks', /* CGS mounts revenueAnalysisActiveLocksV2 */ icon: 'fa-light fa-chart-mixed-up-circle-dollar' },
  { slug: 'investor-shipping', label: 'Investor / Shipping', title: 'Investor/Shipping', section: 'Revenue & capital', gate: null, cgs: 'InvestorShipping', icon: 'fa-light fa-truck-fast' },
  { slug: 'warehouse', label: 'Warehouse Analysis', title: 'Warehouse Analysis', section: 'Revenue & capital', gate: 'corp', cgs: 'WarehouseAnalysis', icon: 'fa-light fa-warehouse' },

  { slug: 'marketing', label: 'Marketing Insights', title: 'Marketing Insights', section: 'Engagement', gate: null, cgs: 'MarketingInsights', icon: 'fa-light fa-envelope-open-text' },
  { slug: 'dialpad', label: 'Dialpad Response', title: 'Dialpad Response Analysis', section: 'Engagement', gate: null, cgs: 'DialpadResponseAnalysis', icon: 'fa-light fa-phone-volume' },
  { slug: 'scenario-builder', label: 'Scenario Builder Metrics', title: 'Scenario Builder Metrics', section: 'Engagement', gate: 'corp', cgs: 'ScenarioBuilderMetrics', icon: 'fa-light fa-calculator' },
  { slug: 'support', label: 'Support Metrics', title: 'Ticket Insights', section: 'Engagement', gate: 'corp', cgs: 'ZendeskInsights', icon: 'fa-light fa-headset' },

  { slug: 'executive', label: 'Executive Dashboard', title: 'Executive Dashboard', section: 'Executive', gate: 'corp', cgs: 'ExeDashBoard', icon: 'fa-light fa-briefcase' },
  { slug: 'baldwin', label: 'Baldwin Analysis', title: 'Baldwin Analysis', section: 'Executive', gate: 'baldwin', cgs: 'BaldwinAnalysis', icon: 'fa-light fa-building-columns' }
]

export function insightPath(slug) {
  return `${INSIGHTS_BASE}/${slug}`
}

export function insightPageBySlug(slug) {
  return INSIGHT_PAGES.find(p => p.slug === slug) || null
}

export function readRoles() {
  let raw = null
  try { raw = window?.localStorage?.getItem('active_user_roles') } catch (e) {}
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(x => String(x || '').trim()).filter(Boolean)
    return String(parsed || '').split(',').map(x => x.trim()).filter(Boolean)
  } catch (e) {
    return String(raw).split(',').map(x => x.trim()).filter(Boolean)
  }
}

// The three gates, from the acting user's roles (impersonation-aware, as active_user_roles is).
export function insightGates(roles = readRoles()) {
  const has = r => roles.some(x => String(x).toLowerCase().trim() === r.toLowerCase())
  return {
    corp: has('Corp'),
    revenue: has('Margin Manager') || has('Insights Branch Manager'),
    baldwin: has('Baldwin Insights Manager')
  }
}

export function canSeeInsightPage(page, gates = insightGates()) {
  if (!page) return false
  return !page.gate || !!gates[page.gate]
}

export function visibleInsightPages(gates = insightGates()) {
  if (!isInsightsEnabled()) return []
  return INSIGHT_PAGES.filter(p => canSeeInsightPage(p, gates))
}

// The pages grouped under their section headings, in table order, with empty sections left
// out. Used by both menus.
export function insightSections(gates = insightGates()) {
  const out = []
  visibleInsightPages(gates).forEach(p => {
    let sec = out.find(s => s.section === p.section)
    if (!sec) out.push(sec = { section: p.section, pages: [] })
    sec.pages.push(p)
  })
  return out
}
